import { useToast } from "@chakra-ui/react";
import Persona from "persona";

import { useBoolean } from "@equidefi/hooks";
import InquiryClient from "@equidefi/portals/clients/InquiryClient";
import { useApi } from "@equidefi/portals/hooks/useApi";
import { ENTITY, INDIVIDUAL } from "@equidefi/shared";
import { INQUIRY_TYPE, canBeSilent } from "@equidefi/shared/constants/inquiries";

import { CONFIG } from "../constants/config";

const getInquiryType = (entityType, citizenship) => {
  if (canBeSilent({ citizenship, entityType })) {
    return INQUIRY_TYPE.SILENT;
  }
  if (entityType === INDIVIDUAL) {
    return INQUIRY_TYPE.KYC;
  }
  if (entityType === ENTITY) {
    return INQUIRY_TYPE.KYB;
  }
};

/**
 *
 * @param {string} investmentId
 * @param {{ onComplete: () => void; onCancel: () => void; onError: () => void; onReady: () => void; }} options
 * @returns {{ onStart: () => Promise<void>; isLoading: boolean }}
 */
export const useStartInvestmentInquiry = (
  investmentId,
  { onComplete = () => {}, onCancel = () => {}, onError = () => {}, onReady = () => {} },
) => {
  const inquiryApi = useApi(InquiryClient);
  const toast = useToast();
  const [isLoading, loading] = useBoolean(false);

  const onStart = async (userId, entityType, citizenship, debug) => {
    loading.on();

    try {
      const inquiryType = getInquiryType(entityType, citizenship);

      const data = await inquiryApi.start(investmentId, userId, entityType, citizenship, inquiryType, { debug });
      if (canBeSilent({ citizenship, entityType })) {
        setTimeout(() => {
          onComplete().then(() => {
            loading.off();
          });
        }, 3000);
      } else {
        const params = data.session_token
          ? {
              inquiryId: data.inquiry_id,
              sessionToken: data.session_token,
            }
          : {
              inquiryId: data.inquiry_id,
            };

        const client = new Persona.Client({
          ...params,
          referenceId: data.reference_id,
          fields: {
            ...data.fields,
          },
          environmentId: CONFIG.PERSONA_ENV,
          onReady: () => {
            loading.off();
            onReady();
            client.open();
          },
          onComplete: ({ inquiryId, status, fields }) => {
            onComplete({ inquiryId, status, fields });
          },
          onCancel: ({ inquiryId, sessionToken }) => {
            onCancel({ inquiryId, sessionToken });
          },
          onError: (error) => {
            onError(error);
            loading.off();
            console.error(error);
          },
        });
      }
    } catch (error) {
      loading.off();
      console.error(error);
      toast({
        status: "error",
        description: error.response?.data?.errors?.join(". ") ?? "Something went wrong.",
      });
    }
  };

  return {
    onStart,
    isLoading,
  };
};
